import {
    Chip,
    Container,
    Divider,
    Grid2,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
  } from "@mui/material";
  import Header from "../../components/Header";
  import { Info } from "@mui/icons-material";
  import StyledTypography from "../../ui/StyledTypography";
  import { grey } from "@mui/material/colors";
  import Footer from "../home/Footer";
  import { Variants, motion } from "framer-motion";
  import { useRef } from "react";
  
  const About: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
    const ref = useRef(null)
  
    const containerVariants: Variants = {
      hidden: { opacity: 0, y: 50 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          when: "beforeChildren",
          staggerChildren: 0.2,
        },
      },
    };
  
    const itemVariants: Variants = {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 },
      },
    };
  
    const imageVariants: Variants = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5 },
      },
    };
  
    return (
      <Container
        maxWidth="lg"
        sx={{
          px: 2,
          pt: { xs: 4, md: 8 },
          gap: 4,
        }}
      >
        <Header />
  
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Container
            maxWidth="md"
            sx={{
              height: "80vh",
              width: "100%",
              textAlign: "center",
              py: 10,
            }}
          >
            <Chip
              color="primary"
              icon={<Info sx={{ fontSize: 20 }} />}
              label="About me"
              sx={{ mb: 4 }}
            />
  
            <StyledTypography
              colors={[grey[100], grey[400]]}
              variant={isMobile ? "h3" : "h2"}
              sx={{
                fontWeight: 500,
                position: "relative",
                lineHeight: 1.2,
              }}
              component="div"
              gutterBottom
            >
              Helping You Build the Business and Life You {""}
              <StyledTypography
                colors={[grey[100], grey[400]]}
                variant={isMobile ? "h3" : "h2"}
                style={{
                  fontWeight: 500,
                  fontFamily: "'Modure Placeholder', serif",
                  fontStyle: "italic",
                  display: "inline",
                  lineHeight: 1.2,
                }}
                component="span"
              >
                deserve.
              </StyledTypography>
            </StyledTypography>
  
            <StyledTypography
              colors={[grey[300], grey[700]]}
              variant={isMobile ? "body2" : "body1"}
            >
              Hey, I’m <b>Abdulrahman Othman</b>—as a fellow entrepreneur, I know
              how overwhelming it can be to juggle building a business while keeping
              everything else in check. That's why I’m dedicated to helping you
              create a professional, eye-catching website that works for your
              business, without breaking the bank or eating up all your time. I
              understand just how important your business is, and I'm here to make
              sure you have a digital presence that helps you grow and succeed.
            </StyledTypography>
          </Container>
        </motion.div>
  
        <Grid2 container spacing={4} rowGap={12} my={12}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <motion.div variants={imageVariants} initial="hidden" animate="visible" style={{ height: "100%", width: "100%"}}>
              <Paper
                component={"img"}
                alt="abdulrahman-othman"
                src="/images/Abdulrahman.jpg"
                sx={{
                  borderRadius: 10,
                  height: "100%",
                  width: "100%",
                  objectPosition: "center",
                  objectFit: "cover",
                  maskImage:
                    "linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 1) 100%)",
                }}
              />
            </motion.div>
          </Grid2>
  
          <Grid2 size={{ xs: 12, md: 6 }}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible">
              <Typography
                variant={isMobile ? "h4" : "h3"}
                color="text.primary"
                gutterBottom
              >
                On a{" "}
                <span
                  style={{
                    fontFamily: "'Modure Placeholder', serif",
                    fontStyle: "italic",
                    display: "inline",
                  }}
                >
                  professional
                </span>{" "}
                Note…
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                I’m a passionate <b>Front-End Developer</b> with a focus on building
                dynamic, user-centric web applications using <b>React</b>. Over the
                years, I’ve honed my skills through a combination of self-learning,
                hands-on projects, and online courses, and I’m constantly evolving
                to stay ahead in the tech world. I’ve had the privilege of working
                with clients from a range of industries, helping them craft
                responsive and high-performing websites that stand out.
              </Typography>
  
              <Typography
                variant={isMobile ? "h5" : "h4"}
                color="text.primary"
                gutterBottom
              >
                My Approach to Development
              </Typography>
  
              <Typography variant="body2" color="text.secondary" mb={4}>
                I take pride in writing clean, maintainable code that not only works
                seamlessly but also delivers outstanding performance. My goal is to
                create digital experiences that aren’t just functional, but visually
                appealing and user-friendly. Whether it's designing intuitive
                interfaces with <b>Framer</b> or ensuring a responsive design, my
                mission is to empower businesses with high-quality web solutions
                that drive growth and success.
              </Typography>
  
              <Typography
                variant={isMobile ? "h5" : "h4"}
                color="text.primary"
                gutterBottom
              >
                I’m Obsessed with Learning & Growing
              </Typography>
  
              <Typography variant="body2" color="text.secondary" mb={4}>
                Web development is a never-ending journey, and I thrive on learning
                new technologies and refining my craft. From mastering React to
                diving deep into <b>UX/UI design</b> principles, I’m always on the
                lookout for ways to improve. I love sharing the knowledge I’ve
                gained with others, and I’m passionate about empowering fellow
                developers to achieve their goals by providing valuable insights and
                resources.
              </Typography>
            </motion.div>
          </Grid2>
  
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible">
              <Typography
                variant={isMobile ? "h4" : "h3"}
                color="text.primary"
                gutterBottom
              >
                On a{" "}
                <span
                  style={{
                    fontFamily: "'Modure Placeholder', serif",
                    fontStyle: "italic",
                    display: "inline",
                  }}
                >
                  Personal
                </span>{" "}
                Note…
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                Before diving into web development, I spent several years working in
                graphic design, which shaped my approach to <b>front-end</b>{" "}
                development. This background has given me a unique perspective on
                how to blend creativity with functionality, ensuring every project I
                work on is not only beautiful but also delivers a superior user
                experience.
                <br />
                <br />
                When I’m not coding or designing, you’ll probably find me at the
                gym. Fitness is a huge part of my life, and I believe the same
                discipline and focus that drives my workouts also fuels my work
                ethic. Building a strong body has taught me patience, resilience,
                and the importance of continuous improvement—qualities I bring into
                every project I take on.
              </Typography>
  
              <Typography
                variant={isMobile ? "h5" : "h4"}
                color="text.primary"
                gutterBottom
              >
                Always Evolving
              </Typography>
  
              <Typography variant="body2" color="text.secondary" mb={4}>
                Since I started my journey in web development, I've worked on
                countless projects, constantly refining my skills to deliver better
                results. Whether it’s through coding, design, or problem-solving, my
                commitment to improving and providing value to clients is
                unwavering.
                <br />
                <br /> I’m always excited to take on new challenges, collaborate
                with innovative teams, and continue building websites that make a
                real impact.
              </Typography>
            </motion.div>
          </Grid2>
  
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
            <motion.div variants={imageVariants} initial="hidden" animate="visible">
              <Paper
                component={"img"}
                alt="abdulrahman-othman"
                src="/images/Abdulrahman2.jpg"
                sx={{
                  borderRadius: 10,
                  height: "100%",
                  width: "100%",
                  objectPosition: "center",
                  objectFit: "cover",
                  maskImage:
                    "linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 1) 100%)",
                }}
              />
            </motion.div>
          </Grid2>
        </Grid2>
  
        <Divider />
        <Container
          maxWidth={false}
          sx={{ textAlign: "center", py: 4, maxWidth: 1000 }}
        >
          <motion.div variants={itemVariants} initial="hidden" animate="visible">
            <Typography
              variant={isMobile ? "h4" : "h3"}
              color="text.primary"
              gutterBottom
            >
              Investing in my skills and professional growth has been the best
              decision I’ve made for{" "}
              <span
                style={{
                  fontFamily: "'Modure Placeholder', serif",
                  fontStyle: "italic",
                  display: "inline",
                }}
              >
                myself.
              </span>
            </Typography>
  
            <StyledTypography
              colors={[grey[300], grey[700]]}
              variant={isMobile ? "body2" : "body1"}
              component="div"
            >
              I’ve experimented with numerous tools and <b>frameworks</b> in the
              past, but none have been as intuitive and user-friendly as the
              solutions I offer. The ease of customization, combined with the
              comprehensive resources I provide, ensures that anyone can create a
              high-quality website without a steep learning curve. From the first
              step to the final product, the experience is streamlined, efficient,
              and rewarding. The results speak for themselves—my clients
              consistently see a noticeable improvement in both design and
              functionality.
            </StyledTypography>
          </motion.div>
        </Container>
        <Divider sx={{ mb: 4 }} />
  
        <Footer />
      </Container>
    );
  };
  
  export default About;
  